import React from 'react';
import Typography from 'Components/UI/Typography';
import TextField from 'Components/ComponentLibrary/Components/Uncategorized/TextField';
import StartAdornment from 'Components/ComponentLibrary/Components/Inputs/Search/SearchInput1/StartAdornment';
import {
  FDIcon,
} from '@facet-wealth/design';
import { withStyles } from 'Components/ComponentLibrary/Styles';
import classNames from 'classnames';
import Avatar from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Avatar';
import SubtleCheckboxRow from 'Components/ComponentLibrary/Components/Uncategorized/CheckboxButton/SubtleCheckboxRow';
import PropTypes from 'prop-types';
import { noop } from 'Helpers/PropTypes';
import FormHelperText from '../../../Core/Form/FormHelperText';
import FormControl from '../../../Core/Form/FormControl';
import FormGroup from '../../../Core/Form/FormGroup';

const styles = (theme) => ({
  root: {
    width: '100%',
  },
  option: {
    marginLeft: theme.spacing(5),
    width: `calc(100% - ${theme.spacing(5)}px)`,
  },
  checkedOption: {
    '&:focus-within,&:hover': {
      background: theme.palette.facet.primaryBlueMuted2,
    },
  },
  uncheckedOption: {
    '&:hover, &:focus-within': {
      background: theme.palette.facet.accentBlue21,
    },
  },
  helperText: {},
  groupHeader: {
    borderTop: `1px solid ${theme.palette.facet.accentGray}`,
    paddingTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    color: theme.palette.facet.primaryBlueMuted,
    width: '100%',
  },
  searchContainer: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: 'white',
  },
  searchField: {
    margin: theme.spacing(2, 0),
    '& .MuiInputBase-root.Mui-focused': {
      background: theme.palette.facet.lightGray5,
    },
  },
  inputRoot: {
    height: 'unset',
    color: theme.palette.facet.darkGray,
    background: theme.palette.facet.lightGray5,
    border: 'unset',
    borderRadius: theme.radius.lg,
    '&:hover': {
      background: theme.palette.facet.lightGray,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& ::placeholder': {
      color: theme.palette.facet.darkGray,
    },
  },
  innerInput: {
    padding: theme.spacing(2),
  },
  iconContainer: {
    backgroundColor: theme.palette.facet.primaryBlueMuted,
    marginRight: theme.spacing(2),
  },
  icon: {
    color: 'white',
  },
});

function BigHierarchyMultipicklistLegacy({
  className,
  classes,
  onChange,
  testName,
  value,
  searchLabel,
  iconMapper,
  handleSearchDebounced,
  groups,
  handleMultipleChange,
  helperText,
  error,
  ...rest
}) {
  return (
    <React.Fragment>
      <div className={classes.searchContainer}>
        <TextField
          className={classes.searchField}
          placeholder={searchLabel}
          variant="outlined"
          onChange={handleSearchDebounced}
          test="hierarchy-search"
          size="small"
          InputProps={{
            classes: {
              root: classes.inputRoot,
              input: classes.innerInput,
            },
            startAdornment: (
              <StartAdornment />
            ),
          }}
        />
      </div>
      <FormControl
        error={error}
        className={classNames(classes.root, className)}
        {...rest}
      >
        <FormGroup>
          {Object.entries(groups).map(([groupName, optionsInGroup]) => {
            const { icon: Icon, isLegacy } = iconMapper(groupName) || {};
            const hasOptionsInGroup = optionsInGroup?.filter(({ matchesSearchTerm }) => matchesSearchTerm).length > 0;
            if (!hasOptionsInGroup) {
              return null;
            }
            return (
              <React.Fragment>
                <Typography className={classes.groupHeader} variant="h4" weight="medium">
                  {Icon && (
                    <Avatar className={classes.iconContainer}>
                      {isLegacy ? <Icon className={classes.icon} /> : <FDIcon icon={Icon} color={'white'} /> }
                    </Avatar>
                  )}
                  {groupName}
                </Typography>
                {optionsInGroup.map(({ resource: option, matchesSearchTerm }) => {
                  const isChecked = value.includes(option.id());
                  return matchesSearchTerm ? (
                    <SubtleCheckboxRow
                      test={`big-picklist-control-${testName}-option-${option.id()}`}
                      checked={isChecked}
                      className={classNames(classes.option, {
                        [classes.checkedOption]: isChecked,
                        [classes.uncheckedOption]: !isChecked,
                      }, className)}
                      key={option.id()}
                      value={option.id()}
                      onChange={(evt) => {
                        return onChange(handleMultipleChange(option.id(), evt.target.checked));
                      }}
                      label={option.attr('prompt')}
                    />
                  ) : null;
                })}
              </React.Fragment>
            );
          })}
        </FormGroup>
        {helperText && (
        <FormHelperText className={classes.helperText}>
          {helperText}
        </FormHelperText>
        )}
      </FormControl>
    </React.Fragment>
  );
}

BigHierarchyMultipicklistLegacy.defaultProps = {
  className: '',
  searchLabel: 'Search',
  iconMapper: noop,
};

BigHierarchyMultipicklistLegacy.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  testName: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]).isRequired,
  searchLabel: PropTypes.string,
  iconMapper: PropTypes.func,
  handleSearchDebounced: PropTypes.func.isRequired,
  groups: PropTypes.object.isRequired,
  handleMultipleChange: PropTypes.func.isRequired,
};

export default withStyles(styles, { name: 'BigHierarchyMultipicklistLegacy' })(BigHierarchyMultipicklistLegacy);
